(() => {
  if (customElements.get('rm-reviews-slider')) return;

  class RmReviewsSlider extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.currentIndex = 0;
      this.signature = '';
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = this.motionQuery.matches;
      this.hoverPaused = false;
      this.focusPaused = false;
      this.inViewport = false;
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        this.hoverPaused = true;
        this.scheduleAutoplay();
      }, { signal });
      this.addEventListener('pointerleave', () => {
        this.hoverPaused = false;
        this.scheduleAutoplay();
      }, { signal });
      this.addEventListener('focusin', () => {
        this.focusPaused = true;
        this.scheduleAutoplay();
      }, { signal });
      this.addEventListener('focusout', (event) => {
        if (this.contains(event.relatedTarget)) return;
        this.focusPaused = false;
        this.scheduleAutoplay();
      }, { signal });
      document.addEventListener('visibilitychange', () => this.scheduleAutoplay(), { signal });
      this.motionQuery.addEventListener('change', (event) => {
        this.userPaused = event.matches;
        this.scheduleAutoplay();
      }, { signal });
      this.querySelector('[data-review-autoplay]')?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        if (!this.userPaused) this.focusPaused = false;
        this.scheduleAutoplay();
      }, { signal });
      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.inViewport = entry.isIntersecting;
        this.scheduleAutoplay();
      });
      this.visibilityObserver.observe(this);
      this.querySelector('[data-review-prev]')?.addEventListener('click', () => this.showSlide(this.currentIndex - 1), { signal });
      this.querySelector('[data-review-next]')?.addEventListener('click', () => this.showSlide(this.currentIndex + 1), { signal });

      if (this.dataset.reviewSource === 'manual') {
        this.showSlide(0);
        document.addEventListener('shopify:block:select', (event) => {
          const index = this.slides.indexOf(event.target);
          if (index !== -1) {
            this.userPaused = true;
            this.showSlide(index);
          }
        }, { signal });
        return;
      }

      // Theme editor app sections may be replaced without reloading this section.
      document.addEventListener('shopify:section:load', () => this.discoverSource(), { signal });
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target.contains(this.source)) {
          this.unloadingSection = event.target;
          this.releaseSource();
          this.hidden = true;
          this.scheduleAutoplay();
          this.signature = '';
          this.discoverSource();
        }
      }, { signal });
      this.discoverSource();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      clearInterval(this.discoveryTimer);
      clearTimeout(this.autoplayTimer);
      this.visibilityObserver?.disconnect();
      this.releaseSource();
    }

    get slides() {
      return Array.from(this.querySelectorAll('[data-review-slide]'));
    }

    showSlide(index) {
      const slides = this.slides;
      if (!slides.length) return;
      this.currentIndex = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === this.currentIndex;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        slide.setAttribute('aria-label', `${slideIndex + 1} of ${slides.length}`);
      });
      this.querySelector('.rm-reviews-slider__controls').hidden = slides.length < 2;
      this.scheduleAutoplay();
    }

    scheduleAutoplay() {
      clearTimeout(this.autoplayTimer);
      const enabled = this.dataset.autoplay !== 'false';
      const button = this.querySelector('[data-review-autoplay]');
      if (button) {
        button.hidden = !enabled;
        button.setAttribute('aria-label', this.userPaused ? 'Play slideshow' : 'Pause slideshow');
        button.querySelector('[data-review-pause-icon]').hidden = this.userPaused;
        button.querySelector('[data-review-play-icon]').hidden = !this.userPaused;
      }
      if (!enabled || !this.isConnected || this.hidden || this.slides.length < 2
        || this.userPaused || this.hoverPaused || this.focusPaused || !this.inViewport || document.hidden) return;
      const seconds = Number(this.dataset.autoplayInterval) || 5;
      this.autoplayTimer = setTimeout(() => this.showSlide(this.currentIndex + 1), Math.max(3, seconds) * 1000);
    }

    releaseSource() {
      this.sourceObserver?.disconnect();
      if (this.sourceSection) {
        this.sourceSection.classList.remove('rm-reviews-slider-source');
        this.sourceSection.inert = this.originalInert;
        if (this.originalAriaHidden === null) this.sourceSection.removeAttribute('aria-hidden');
        else this.sourceSection.setAttribute('aria-hidden', this.originalAriaHidden);
      }
      this.source = null;
      this.sourceSection = null;
      this.observedShadowRoot = null;
    }

    discoverSource() {
      clearInterval(this.discoveryTimer);
      const discover = () => {
        const source = document.querySelector('.gw-rv-reviews-slider-widget-placeholder');
        if (!source || this.unloadingSection?.contains(source)) return;
        this.unloadingSection = null;
        if (source !== this.source) {
          this.releaseSource();
          this.source = source;
          this.signature = '';
          this.sourceSection = source.closest('.shopify-section') || source;
          this.originalInert = this.sourceSection.inert;
          this.originalAriaHidden = this.sourceSection.getAttribute('aria-hidden');
          this.sourceSection.classList.add('rm-reviews-slider-source');
          this.sourceSection.inert = true;
          this.sourceSection.setAttribute('aria-hidden', 'true');
          this.sourceObserver = new MutationObserver(() => this.readReviews());
          this.sourceObserver.observe(source, { childList: true, subtree: true, characterData: true, attributes: true });
        }

        // attachShadow itself does not trigger a document MutationObserver.
        // Poll until it exists, then observe the actual Growave rendering root.
        if (source.shadowRoot && source.shadowRoot !== this.observedShadowRoot) {
          this.observedShadowRoot = source.shadowRoot;
          this.sourceObserver.observe(source.shadowRoot, { childList: true, subtree: true, characterData: true, attributes: true });
        }
        this.readReviews();
        if (source.shadowRoot || source.querySelector('.gw-rv-slider-item')) {
          clearInterval(this.discoveryTimer);
        }
      };
      // There is deliberately no timeout that switches to unrelated manual reviews.
      this.discoveryTimer = setInterval(discover, 500);
      discover();
    }

    safeUrl(value) {
      if (!value) return '';
      try {
        const url = new URL(value, window.location.href);
        return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
      } catch {
        return '';
      }
    }

    readReviews() {
      const renderingRoot = this.source?.shadowRoot || this.source;
      if (!renderingRoot) return;
      const seen = new Set();
      const reviews = Array.from(renderingRoot.querySelectorAll('.gw-rv-slider-item')).map((slide) => {
        const text = (selector) => slide.querySelector(selector)?.textContent.trim() || '';
        const image = slide.querySelector('.gw-rv-slider-item__image-wrapper img');
        const product = slide.querySelector('a[href*="/products/"]');
        const ratingLabel = slide.querySelector('.gw-rv-avg-rating')?.getAttribute('aria-label') || '';
        const rating = Number.parseFloat(ratingLabel.match(/\d+(?:\.\d+)?/)?.[0]);
        return {
          author: text('.gw-rv-slider-item__review-author p'),
          quote: text('.gw-rv-slider-item__body'),
          rating: Number.isFinite(rating) && rating >= 0 && rating <= 5 ? rating : null,
          image: this.safeUrl(image?.getAttribute('src') || image?.getAttribute('data-src')),
          imageAlt: image?.getAttribute('alt') || '',
          productUrl: this.safeUrl(product?.getAttribute('href')),
          productTitle: product?.textContent.trim() || image?.getAttribute('alt') || '',
        };
      }).filter((review) => {
        const key = JSON.stringify(review);
        if (!review.quote || seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      const signature = JSON.stringify(reviews);
      if (signature === this.signature) return;
      this.signature = signature;
      if (!reviews.length) {
        this.hidden = true;
        this.scheduleAutoplay();
        return;
      }

      const fragment = document.createDocumentFragment();
      const template = this.querySelector('[data-review-template]');
      reviews.forEach((review) => {
        const slide = template.content.firstElementChild.cloneNode(true);
        slide.querySelector('[data-review-quote]').textContent = review.quote;
        const author = slide.querySelector('[data-review-author]');
        author.textContent = review.author ? `— ${review.author}` : '';
        author.hidden = !review.author;
        const stars = slide.querySelector('[data-review-rating]');
        stars.hidden = review.rating === null;
        if (review.rating !== null) {
          stars.setAttribute('aria-label', `${review.rating} out of 5 stars`);
          for (let i = 1; i <= 5; i += 1) {
            const star = document.createElement('span');
            star.setAttribute('aria-hidden', 'true');
            star.textContent = i <= Math.round(review.rating) ? '★' : '☆';
            stars.appendChild(star);
          }
        }
        const media = slide.querySelector('[data-review-media]');
        media.hidden = !review.image;
        slide.classList.toggle('rm-reviews-slider__slide--text-only', !review.image);
        if (review.image) {
          const image = slide.querySelector('[data-review-image]');
          image.src = review.image;
          image.alt = review.imageAlt;
        }
        if (review.productUrl && review.productTitle) {
          const product = slide.querySelector('[data-review-product]');
          product.href = review.productUrl;
          product.textContent = review.productTitle;
          product.hidden = false;
        }
        fragment.appendChild(slide);
      });
      this.slides.forEach((slide) => slide.remove());
      this.querySelector('.rm-reviews-slider__inner').prepend(fragment);
      this.showSlide(this.currentIndex);
      this.hidden = false;
      this.dataset.loadedSource = 'growave';
      this.scheduleAutoplay();
    }
  }

  customElements.define('rm-reviews-slider', RmReviewsSlider);
})();
